import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../api/axios';
import { Search, Clock, CheckCircle2, XCircle, Smartphone, DollarSign, Package, MapPin, User, FileText, Loader2, RefreshCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const TrackOrder = () => {
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Auto-refresh effect
  useEffect(() => {
    if (order) {
      // Set initial last updated time
      if (!lastUpdated) setLastUpdated(new Date().toLocaleTimeString());

      const interval = setInterval(() => {
        trackOrder(order.orderNumber, customerPhone, true); // Silent refresh
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [order, lastUpdated, customerPhone]);

  useEffect(() => {
    const orderParam = searchParams.get('order');
    const phoneParam = searchParams.get('phone');
    if (orderParam && phoneParam) {
      setOrderNumber(orderParam);
      setCustomerPhone(phoneParam);
      trackOrder(orderParam, phoneParam);
    }
  }, [searchParams]);

  const trackOrder = async (orderNum, phone, silent = false) => {
    if (!silent) {
      setLoading(true);
      setError('');
      setOrder(null);
    }

    try {
      const response = await axios.get(`/orders/track/${orderNum}?phone=${phone}`);
      setOrder(response.data);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      if (!silent) {
        setError(error.response?.data?.message || 'Order not found. Please check your details.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !customerPhone.trim()) {
      setError('Both order number and phone number are required.');
      return;
    }
    trackOrder(orderNumber, customerPhone);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="text-green-500 w-12 h-12" />;
      case 'cancelled':
        return <XCircle className="text-red-500 w-12 h-12" />;
      case 'ready':
        return <Package className="text-blue-500 w-12 h-12" />;
      case 'preparing':
        return <Loader2 className="text-gold-500 w-12 h-12 animate-spin" />;
      case 'accepted':
        return <CheckCircle2 className="text-green-500 w-12 h-12" />; // Or a different icon for accepted
      default:
        return <Clock className="text-gray-400 w-12 h-12" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'cancelled': return 'text-red-500';
      case 'ready': return 'text-blue-500';
      case 'preparing': return 'text-gold-500';
      case 'accepted': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Order Received',
      accepted: 'Order Accepted',
      preparing: 'Preparing Your Food',
      ready: 'Ready for Pickup/Delivery',
      completed: 'Order Completed',
      cancelled: 'Order Cancelled'
    };
    return statusMap[status] || status;
  };

  return (
    <div className="min-h-screen bg-background pb-20 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gold-900/10 rounded-full blur-3xl opacity-20" />
      </div>

      <div className="container mx-auto px-6 pt-40 pb-12 relative z-10">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-center text-white mb-8 font-serif"
        >
          Track Your Order
        </motion.h1>

        <div className="max-w-3xl mx-auto">
          {/* Search Form */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-gray-900/60 border-gold-500/30 backdrop-blur-md mb-8">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4">
                  <div className="flex-grow space-y-2">
                    <label className="text-gold-100 font-medium">Order Number</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                      <Input
                        type="text"
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value)}
                        placeholder="e.g., ORD000001"
                        className="pl-10 bg-gray-800/50 border-gray-700 text-white focus:border-gold-500 uppercase font-mono tracking-wider"
                      />
                    </div>
                  </div>
                  <div className="flex-grow space-y-2">
                    <label className="text-gold-100 font-medium">Phone Number</label>
                    <div className="relative">
                      <Smartphone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                      <Input
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="03XXXXXXXXX"
                        className="pl-10 bg-gray-800/50 border-gray-700 text-white focus:border-gold-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full md:w-auto bg-gold-500 text-black font-bold h-10 shadow-lg hover:bg-gold-400 min-w-[120px]"
                    >
                      {loading ? <Loader2 className="animate-spin w-5 h-5" /> : 'Track Order'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8"
              >
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-center text-red-200">
                  <p>{error}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Order Details */}
          <AnimatePresence>
            {order && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Status Card */}
                <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-md overflow-hidden relative">
                  <div className={`absolute top-0 left-0 w-1 h-full ${order.status === 'completed' ? 'bg-green-500' :
                    order.status === 'cancelled' ? 'bg-red-500' : 'bg-gold-500'
                    }`} />
                  <CardContent className="p-8 text-center relative">
                    {lastUpdated && (
                      <div className="absolute top-4 right-4 flex items-center gap-1 text-xs text-gray-500">
                        <RefreshCcw className="w-3 h-3" /> Updated: {lastUpdated}
                      </div>
                    )}

                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 10 }}
                      className="flex justify-center mb-4"
                    >
                      <div className={`w-24 h-24 rounded-full flex items-center justify-center bg-gray-800 border-4 border-gray-700 shadow-xl ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                      </div>
                    </motion.div>

                    <h2 className={`text-3xl font-bold mb-2 font-serif ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </h2>

                    <div className="bg-black/30 inline-block px-4 py-1.5 rounded-full border border-white/5 mb-2">
                      <p className="text-gray-300 font-mono tracking-wider">#{order.orderNumber}</p>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Placed on {new Date(order.createdAt).toLocaleString('en-PK', { dateStyle: 'long', timeStyle: 'short' })}
                    </p>
                  </CardContent>
                </Card>

                {/* Progress Bar (Visual representation based on simplified statuses) */}
                {order.status !== 'cancelled' && (
                  <div className="flex justify-between relative px-4 sm:px-12 mb-8">
                    {/* Connecting Line */}
                    <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-800 -z-10 transform -translate-y-1/2"></div>
                    <div
                      className="absolute top-1/2 left-0 h-1 bg-gold-500 -z-10 transform -translate-y-1/2 transition-all duration-1000"
                      style={{
                        width: order.status === 'pending' ? '10%' :
                          order.status === 'accepted' ? '35%' :
                            order.status === 'preparing' ? '60%' :
                              order.status === 'ready' ? '85%' :
                                order.status === 'completed' ? '100%' : '0%'
                      }}
                    ></div>

                    {['pending', 'preparing', 'ready', 'completed'].map((step, i) => {
                      const isCompleted = ['pending', 'accepted', 'preparing', 'ready', 'completed'].indexOf(order.status) >= ['pending', 'preparing', 'ready', 'completed'].indexOf(step);

                      return (
                        <div key={step} className="flex flex-col items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 z-10 transition-colors duration-500 ${isCompleted ? 'bg-gold-500 border-gold-500' : 'bg-gray-900 border-gray-600'}`}></div>
                          <span className={`text-xs capitalize hidden sm:block ${isCompleted ? 'text-gold-500' : 'text-gray-600'}`}>{step}</span>
                        </div>
                      )
                    })}
                  </div>
                )}


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Order Items */}
                  <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm">
                    <CardHeader className="border-b border-gray-800 pb-4">
                      <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                        <Package className="w-5 h-5 text-gold-500" /> Order Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between items-start border-b border-gray-800 pb-3 last:border-0">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="bg-gray-800 text-gray-300 font-mono">{item.quantity}x</Badge>
                                <p className="text-white font-medium">{item.name}</p>
                              </div>
                              {item.selectedOptions && item.selectedOptions.length > 0 && (
                                <p className="text-xs text-gold-400 mt-1 ml-9">
                                  + {item.selectedOptions.map(opt => opt.name).join(', ')}
                                </p>
                              )}
                            </div>
                            <p className="text-gray-300 font-medium">Rs. {item.itemTotal.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-800">
                        <span className="text-lg font-bold text-white">Total Amount</span>
                        <span className="text-2xl font-bold text-gold-500">
                          Rs. {order.totalAmount.toLocaleString()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="space-y-6">
                    {/* Payment Method */}
                    <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm">
                      <CardHeader className="border-b border-gray-800 pb-4">
                        <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-gold-500" /> Payment Info
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className={`p-3 rounded-full ${order.paymentMethod === 'easypaisa' ? 'bg-green-500/20' : 'bg-gold-500/20'}`}>
                            {order.paymentMethod === 'easypaisa' ? <Smartphone className="text-green-500 w-6 h-6" /> : <DollarSign className="text-gold-500 w-6 h-6" />}
                          </div>
                          <div>
                            <p className="text-white font-bold capitalize">{order.paymentMethod === 'easypaisa' ? 'Easypaisa' : 'Cash on Delivery'}</p>
                            {order.paymentMethod === 'easypaisa' && (
                              <div className="mt-1">
                                {order.paymentVerified ? (
                                  <span className="flex items-center gap-1 text-green-400 text-sm font-bold"><CheckCircle2 className="w-3 h-3" /> Verified</span>
                                ) : (
                                  <span className="flex items-center gap-1 text-yellow-500 text-sm"><Clock className="w-3 h-3" /> Pending Verification</span>
                                )}
                              </div>
                            )}
                            {order.paymentMethod === 'cash' && <p className="text-gray-400 text-xs">Pay upon receiving</p>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Customer Details */}
                    <Card className="bg-gray-900/60 border-gray-800 backdrop-blur-sm">
                      <CardHeader className="border-b border-gray-800 pb-4">
                        <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                          <User className="w-5 h-5 text-gold-500" /> Delivery Info
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-4">
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-gray-500" />
                          <p className="text-gray-300">{order.customerName}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Smartphone className="w-4 h-4 text-gray-500" />
                          <p className="text-gray-300 font-mono">{order.customerPhone}</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-gray-500 mt-1" />
                          <p className="text-gray-300">{order.customerAddress}</p>
                        </div>
                        {order.notes && (
                          <div className="bg-gray-800/50 p-3 rounded-lg mt-2 border border-white/5">
                            <div className="flex items-start gap-2">
                              <FileText className="w-3 h-3 text-gold-500 mt-1" />
                              <div>
                                <p className="text-xs text-gold-500 font-bold uppercase mb-1">Notes</p>
                                <p className="text-sm text-gray-400 italic">"{order.notes}"</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;

